import { create } from 'zustand';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { point as turfPoint } from '@turf/helpers';
import type { WeatherStation } from '../types/weatherStation';
import type { StationSearchType } from '../types/stationSearch';
import { mapSearchTypeToTagKey, describeSearchValues, tagKeyToSearchType } from '../types/stationSearch';
import { createLogger } from '../utils/logger';
import { weatherStationAPI, APIError, ErrorType } from '../services/WeatherStationAPI';
import { useStationDataStore } from './stationDataStore';
import { useMapStore } from './mapStore';
import { useTableStore } from './tableStore';

const log = createLogger('SearchStore');

// ponytail: activeSearchMethod uses the union from AppState, not StationSearchType
// (StationSearchType is for the unified station/tag bar only).
type ActiveSearchMethod = 'stationCode' | 'coordinates' | 'country' | 'tags' | 'station' | null;

export interface SearchState {
  searchQuery: string;
  isSearching: boolean;
  searchError: string | null;
  activeSearchMethod: ActiveSearchMethod;
  searchCenter: { latitude: number; longitude: number } | null;

  /** The committed spatial region for a Draw Box / Draw Shape search.
   * `feature` is the GeoJSON polygon the results were filtered against (also
   * rendered on the map as the highlighted region); `label` is a human tag
   * ("Box" / "Custom shape") for the results header. Null when the
   * active coordinate search is radial or when there's no search. Mutually
   * exclusive with `searchCenter` — committing a region clears the radius center
   * and vice-versa, so the map never shows a radius ring AND a polygon at once. */
  searchRegion: { feature: GeoJSON.Feature<GeoJSON.Polygon>; label: string } | null;

  /** True when the last completed search returned zero stations. Distinct from
   * `searchError`, which is reserved for actual failures (network, auth, service).
   * A zero-result response is a valid empty state, not an error — this flag drives
   * an EmptyState UI instead of the amber error banner. Reset on new search + clear. */
  searchYieldedNoResults: boolean;

  /** PREPOSITIONAL PHRASE naming what the last committed search asked for — "for
   * MGID 1506235", "within 50km of 52.0934, 4.4745", "inside the drawn box", "in
   * Zuid-Holland". Empty states append it to "No stations found …" so the answer
   * reads as an answer instead of a generic "no results"; the phrase carries its
   * own preposition because "found" is the one verb frame that fits identifiers
   * and places alike (see SearchPage.outcome.ts). Null until a search is
   * committed; cleared by clearSearch. Set by EVERY search action so all methods
   * speak the same way. */
  lastQueryLabel: string | null;

  /** Country search session state — stores the selected location for the current country search.
   * Requirements: 9.2 */
  countrySearchState: {
    selectedCountry: string;
    selectedState: string;
    selectedCity: string;
  } | null;

  /** GeoJSON data for country boundary overlays rendered by MapView.
   * Stored in Zustand so MapView can read it as a prop — no event dispatch needed.
   * Requirements: 6.4, 8.1, 8.3 */
  countryBoundaryLayers: any[] | null;
}

export interface SearchActions {
  setSearchQuery: (query: string) => void;
  setActiveSearchMethod: (method: ActiveSearchMethod) => void;
  searchStation: (code: string) => Promise<void>;
  searchByTags: (tagKey: string, tagValues: string[]) => Promise<void>;
  searchByCoordinates: (latitude: number, longitude: number, radius?: number) => Promise<void>;
  /** Search stations inside an arbitrary polygon region (Draw Box or Draw
   * Shape). Filters the already-loaded station list client-side — no backend call.
   * Requirements: spatial-search (bbox + draw). */
  searchByRegion: (feature: GeoJSON.Feature<GeoJSON.Polygon>, label: string) => void;
  searchByStationTag: (searchType: StationSearchType, values: string[]) => Promise<void>;
  /** Execute a country search using pre-fetched station data.
   * Requirements: 5.1, 5.2, 5.3, 5.5, 5.6, 9.1, 9.2, 9.3, 9.4 */
  searchByCountry: (country: string, stations: WeatherStation[], state?: string, city?: string) => void;
  /** Store fetched boundary GeoJSON for MapView to render. Requirements: 6.4, 8.1 */
  setCountryBoundaryLayers: (layers: any[] | null) => void;
  clearSearch: () => void;
}

export type SearchStore = SearchState & SearchActions;

// ponytail: callbacks for cross-store error/warning display.
// Set externally by app init so searchStore stays decoupled.
let onError: ((message: string, actionLabel?: string, onAction?: () => void) => void) | undefined;
let onWarning: ((message: string) => void) | undefined;

export function setSearchErrorHandler(handler: typeof onError) { onError = handler; }
export function setSearchWarningHandler(handler: typeof onWarning) { onWarning = handler; }

// ── Request-generation guard ────────────────────────────────────────────────
// A monotonic token bumped on every search START and on clearSearch/method-switch.
// Each async search action captures the token at entry; before it commits ANY
// result/isSearching/side-effect (in BOTH the success and catch branches) it checks
// it still owns the latest generation. This prevents: (a) a slow in-flight fetch
// landing stale results on a newer search or a different method, and (b) a
// method-switch/Clear mid-fetch leaving isSearching stuck true when the orphaned
// promise later resolves. `bumpSearchGeneration()` is also called by clearSearch and
// by SearchPage's method change to invalidate anything in flight.
let searchGeneration = 0;
export function bumpSearchGeneration(): number { return ++searchGeneration; }

export const useSearchStore = create<SearchStore>((set, get) => ({
  // --- State (defaults match appStore initialState) ---
  searchQuery: '',
  isSearching: false,
  searchError: null,
  activeSearchMethod: null,
  searchCenter: null,
  searchRegion: null,
  searchYieldedNoResults: false,
  lastQueryLabel: null,
  countrySearchState: null,
  countryBoundaryLayers: null,

  // --- Actions ---

  setSearchQuery: (query) => {
    set({ searchQuery: query });
  },

  // Task 3.1: Clear countrySearchState when switching away from 'country' (Requirements: 9.3)
  setActiveSearchMethod: (method) => {
    const updates: Partial<SearchState> = {
      activeSearchMethod: method,
      searchError: null,
    };
    // Clear country search state when switching to a non-country method
    if (method !== 'country' && get().countrySearchState !== null) {
      updates.countrySearchState = null;
      updates.countryBoundaryLayers = null;
    }
    set(updates);
  },

  /** Store fetched boundary GeoJSON for MapView to render. Requirements: 6.4, 8.1 */
  setCountryBoundaryLayers: (layers: any[] | null) => {
    set({ countryBoundaryLayers: layers });
  },

  // Search for a station by code (supports multiple codes separated by comma, space, or tab)
  searchStation: async (code) => {
    if (!code.trim()) return;

    // Parse multiple station codes (comma, space, or tab separated)
    const codes = code
      .split(/[,\s\t]+/)
      .map(c => c.trim())
      .filter(c => c.length > 0);

    if (codes.length === 0) return;

    const gen = bumpSearchGeneration();
    // Don't wipe existing results here — a refine keeps the current list visible
    // under the spinner instead of flashing empty, and a superseded search won't
    // blank the view. Results are replaced only on this generation's success.
    set({
      isSearching: true,
      searchError: null,
      searchYieldedNoResults: false,
      lastQueryLabel: `for ${describeSearchValues('stationCode', codes)}`,
    });

    const stationData = useStationDataStore.getState();

    try {
      const api = weatherStationAPI;
      const validStations: WeatherStation[] = [];
      const batchSize = 50;

      for (let i = 0; i < codes.length; i += batchSize) {
        const batch = codes.slice(i, i + batchSize);
        log.debug(`Fetching batch ${Math.floor(i / batchSize) + 1}/${Math.ceil(codes.length / batchSize)}: ${batch.length} stations`);

        try {
          const batchResults = await api.getStationsByCodes(batch);
          validStations.push(...batchResults);
        } catch (batchError) {
          log.warn(`⚠️ Batch fetch failed for ${batch.length} stations, skipping batch`);
        }

        if (i + batchSize < codes.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      // A newer search (or a Clear / method switch) started while we awaited —
      // discard this stale result entirely so it can't overwrite the current view.
      if (gen !== searchGeneration) return;

      if (validStations.length === 0) {
        // Zero-result is an empty state, not an error (mirrors searchByCoordinates).
        stationData.setSelectedStation(null);
        stationData.setSelectedStations([]);
        set({ isSearching: false, searchError: null, searchYieldedNoResults: true });
        return;
      }

      log.info(`✅ Successfully fetched ${validStations.length} out of ${codes.length} stations`);

      // The live weatherstations proxy (used above) returns raw ObsWrapper data with
      // no GADM region enrichment, so stateType/admin/etc. come back empty and the
      // table's "Region Type" column is blank for station-code search results. The
      // bulk station-metadata dataset (loaded into stationDataStore for map/coordinate
      // search) already has this enrichment — backfill it here by station code.
      const bulkByCode = new Map(
        useStationDataStore.getState().allStations.map(s => [s.stationCode, s])
      );
      const enrichedStations = validStations.map(station => {
        const bulk = bulkByCode.get(station.stationCode);
        if (!bulk) return station;
        return {
          ...station,
          city: station.city || bulk.city,
          stateType: station.stateType || bulk.stateType,
          cityType: station.cityType || bulk.cityType,
          districtType: station.districtType || bulk.districtType,
          countryCode: station.countryCode || bulk.countryCode,
          stateCode: station.stateCode || bulk.stateCode,
          cityCode: station.cityCode || bulk.cityCode,
          districtCode: station.districtCode || bulk.districtCode,
          admin: station.admin || bulk.admin,
        };
      });

      // Cross-store: update station data
      stationData.setSelectedStation(enrichedStations[0]);
      stationData.setSelectedStations(enrichedStations);

      set({ isSearching: false, searchError: null, searchYieldedNoResults: false });

      // Task 5.1 & 5.2: Handle single vs multiple result behavior for station code search
      // (Requirements: 1.1, 1.2, 1.3, 1.4, 9.1, 9.2, 9.3)
      if (enrichedStations.length === 1) {
        const station = enrichedStations[0];
        log.info(`[searchStation] Single result found: ${station.stationCode}, auto-zooming to level 12`);

        window.dispatchEvent(new CustomEvent('openStationPopup', {
          detail: {
            stationCode: station.stationCode,
            station: station,
            shouldZoom: true,
            shouldRecenter: false,
            currentZoom: 2,
            targetZoom: 12,
          }
        }));

        useMapStore.getState().selectStation(station.stationCode, { lat: station.latitude, lon: station.longitude }, 12);
      } else if (enrichedStations.length > 1) {
        log.info(`[searchStation] Multiple results found: ${enrichedStations.length} stations, fitting bounds`);

        window.dispatchEvent(new CustomEvent('fitStationBounds', {
          detail: { stations: enrichedStations, padding: 50 },
        }));

        useMapStore.getState().selectStation(null);
      }
    } catch (error) {
      if (gen !== searchGeneration) return; // superseded — don't surface a stale error
      let errorMessage = 'An unexpected error occurred';
      let shouldShowRetry = false;

      if (error instanceof APIError) {
        errorMessage = error.message;
        shouldShowRetry = error.type === ErrorType.NETWORK_ERROR ||
                         error.type === ErrorType.SERVICE_UNAVAILABLE ||
                         error.type === ErrorType.AUTHENTICATION_FAILED;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      set({ isSearching: false, searchError: errorMessage });
      useStationDataStore.getState().setSelectedStation(null);
      useStationDataStore.getState().setSelectedStations([]);

      if (onError && shouldShowRetry) {
        onError(errorMessage, 'Retry', () => { get().searchStation(code); });
      }
    }
  },

  // Search for stations by tags with tag key and values
  searchByTags: async (tagKey: string, tagValues: string[]) => {
    if (tagValues.length === 0) return;

    const gen = bumpSearchGeneration();
    // This action receives an API tag key, not a StationSearchType, so recover the
    // type to get the HUMAN label ('mgID' → 'MGID'). A null means the key maps to no
    // single type (stationCode/deviceId/allTags all use ''), in which case the values
    // stand alone rather than being prefixed with a wrong one.
    const searchTypeForLabel = tagKeyToSearchType(tagKey);
    set({
      isSearching: true,
      searchError: null,
      searchYieldedNoResults: false,
      lastQueryLabel: `for ${searchTypeForLabel
        ? describeSearchValues(searchTypeForLabel, tagValues)
        : tagValues.join(', ')}`,
    });

    const stationData = useStationDataStore.getState();

    try {
      const api = weatherStationAPI;
      const stations = await api.getStationsByTags(tagKey, tagValues);

      if (gen !== searchGeneration) return; // superseded by a newer search / clear

      if (stations.length === 0) {
        // Empty state, not an error (parity with the other zero-result paths).
        stationData.setSelectedStation(null);
        stationData.setSelectedStations([]);
        set({ isSearching: false, searchError: null, searchYieldedNoResults: true });
        return;
      }

      // Filter to only primary results for the details panel
      const primaryStations = stations.filter(s => s.isPrimaryResult !== false);

      // Cross-store: update station data
      stationData.setSelectedStation(primaryStations[0]);
      stationData.setSelectedStations(primaryStations);

      set({ isSearching: false, searchError: null, searchYieldedNoResults: false });

      // Task 5.3 & 5.4: Handle single vs multiple result behavior for tag search
      if (primaryStations.length === 1) {
        const station = primaryStations[0];
        log.info(`[searchByTags] Single result found: ${station.stationCode}, auto-zooming to level 12`);

        window.dispatchEvent(new CustomEvent('openStationPopup', {
          detail: {
            stationCode: station.stationCode,
            station: station,
            shouldZoom: true,
            shouldRecenter: false,
            currentZoom: 2,
            targetZoom: 12,
          }
        }));

        useMapStore.getState().selectStation(station.stationCode, { lat: station.latitude, lon: station.longitude }, 12);
      } else if (primaryStations.length > 1) {
        log.info(`[searchByTags] Multiple results found: ${primaryStations.length} stations, fitting bounds`);

        window.dispatchEvent(new CustomEvent('fitStationBounds', {
          detail: { stations: primaryStations, padding: 50 },
        }));

        useMapStore.getState().selectStation(null);
      }
    } catch (error) {
      let errorMessage = 'An unexpected error occurred';
      let shouldShowRetry = false;

      if (error instanceof APIError) {
        errorMessage = error.message;
        shouldShowRetry = error.type === ErrorType.NETWORK_ERROR ||
                         error.type === ErrorType.SERVICE_UNAVAILABLE ||
                         error.type === ErrorType.AUTHENTICATION_FAILED;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      set({ isSearching: false, searchError: errorMessage });

      if (onError && shouldShowRetry) {
        onError(errorMessage, 'Retry', () => { get().searchByTags(tagKey, tagValues); });
      }
    }
  },

  // Search for stations by coordinates (radius search)
  searchByCoordinates: async (latitude: number, longitude: number, radiusKm: number = 50) => {
    const gen = bumpSearchGeneration();
    // Keep existing results under the spinner during a refine; only replace on success.
    // Clear any committed region so the radial ring and a polygon never coexist.
    set({
      isSearching: true,
      searchError: null,
      searchCenter: null,
      searchRegion: null,
      searchYieldedNoResults: false,
      lastQueryLabel: `within ${radiusKm}km of ${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
    });

    const stationData = useStationDataStore.getState();

    try {
      const api = weatherStationAPI;
      const stations = await api.getStationsByCoordinates(latitude, longitude, radiusKm);

      if (gen !== searchGeneration) return; // superseded by a newer search / clear

      if (stations.length === 0) {
        // Zero-result is an empty state, not an error. Set the searchCenter (so the map
        // still shows the search pin) and raise the flag; UI renders <EmptyState>.
        // Explicitly clear results here (we no longer wipe them at the start).
        stationData.setSelectedStation(null);
        stationData.setSelectedStations([]);
        set({
          isSearching: false,
          searchError: null,
          searchCenter: { latitude, longitude },
          searchYieldedNoResults: true,
        });
        return;
      }

      // Cross-store: update station data (don't select a single station for coordinates search)
      stationData.setSelectedStations(stations);

      set({
        isSearching: false,
        searchError: null,
        searchCenter: { latitude, longitude },
        searchYieldedNoResults: false,
      });

      // Set initial sort for coordinates search via tableStore (Requirements: 19.1, 19.4)
      useTableStore.setState({ sortColumn: 'distance', sortDirection: 'asc' });

      // Task 5.5: Implement coordinate search initial display
      log.info(`[searchByCoordinates] Found ${stations.length} stations within ${radiusKm}km, fitting bounds`);

      window.dispatchEvent(new CustomEvent('fitStationBounds', {
        detail: {
          stations: stations,
          searchCenter: { latitude, longitude },
          padding: 50,
        }
      }));

      useMapStore.getState().selectStation(null);
    } catch (error) {
      if (gen !== searchGeneration) return; // superseded — don't surface a stale error
      let errorMessage = 'An unexpected error occurred';
      let shouldShowRetry = false;

      if (error instanceof APIError) {
        errorMessage = error.message;
        shouldShowRetry = error.type === ErrorType.NETWORK_ERROR ||
                         error.type === ErrorType.SERVICE_UNAVAILABLE ||
                         error.type === ErrorType.AUTHENTICATION_FAILED;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      set({ isSearching: false, searchError: errorMessage, searchCenter: null });

      if (onError && shouldShowRetry) {
        onError(errorMessage, 'Retry', () => { get().searchByCoordinates(latitude, longitude, radiusKm); });
      }
    }
  },

  /**
   * Search stations inside an arbitrary polygon region (Draw Box or Draw
   * Shape). Unlike searchByCoordinates this needs NO backend call: it filters the
   * station list already loaded at app init (stationDataStore.allStations, ~136k)
   * client-side with a turf point-in-polygon test. Per product decision, results
   * are NOT capped — every station inside the region is selected and rendered.
   *
   * Synchronous (like searchByCountry): still bumps the generation guard so an
   * in-flight radial/station fetch resolving afterward can't clobber these results.
   * Sets searchRegion and clears searchCenter so the two coordinate sub-modes stay
   * mutually exclusive on the map.
   */
  searchByRegion: (feature: GeoJSON.Feature<GeoJSON.Polygon>, label: string) => {
    bumpSearchGeneration();
    set({
      isSearching: true,
      searchError: null,
      searchYieldedNoResults: false,
      lastQueryLabel: `inside the ${label.toLowerCase()}`,
    });

    const stationData = useStationDataStore.getState();

    // Region search filters the client-side station list; if it hasn't loaded yet
    // (or the initial load failed), an empty list would otherwise be reported as
    // "no stations found inside the region" — a false negative. Surface the real
    // cause instead and leave searchRegion unset so the user can retry once loaded.
    if (stationData.allStations.length === 0) {
      const stillLoading = stationData.isLoadingAllStations;
      log.warn(`[searchByRegion] ${label}: station list not ready (loading=${stillLoading}); aborting region search`);
      set({
        isSearching: false,
        searchError: stillLoading
          ? 'Station data is still loading — try your region search again in a moment.'
          : 'Station data is unavailable right now. Please refresh and try again.',
        searchYieldedNoResults: false,
      });
      return;
    }

    try {
      const allStations = stationData.allStations;
      const matched: WeatherStation[] = [];
      for (const s of allStations) {
        if (isNaN(s.latitude) || isNaN(s.longitude)) continue;
        if (s.latitude < -90 || s.latitude > 90 || s.longitude < -180 || s.longitude > 180) continue;
        // turf expects [lng, lat]; a station on the polygon edge counts as inside.
        if (booleanPointInPolygon(turfPoint([s.longitude, s.latitude]), feature)) {
          matched.push(s);
        }
      }

      log.info(`[searchByRegion] ${label}: ${matched.length} stations inside region (of ${allStations.length} loaded)`);

      if (matched.length === 0) {
        stationData.setSelectedStation(null);
        stationData.setSelectedStations([]);
        set({
          isSearching: false,
          searchError: null,
          searchCenter: null,
          searchRegion: { feature, label },
          searchYieldedNoResults: true,
        });
        return;
      }

      stationData.setSelectedStations(matched);
      set({
        isSearching: false,
        searchError: null,
        searchCenter: null,
        searchRegion: { feature, label },
        searchYieldedNoResults: false,
      });

      // No inherent distance-from-center for a region; sort by station code so the
      // table opens in a stable order (radial search sorts by distance instead).
      useTableStore.setState({ sortColumn: 'stationCode', sortDirection: 'asc' });

      window.dispatchEvent(new CustomEvent('fitStationBounds', {
        detail: { stations: matched, padding: 50 },
      }));

      useMapStore.getState().selectStation(null);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Region search failed. Please try again.';
      log.error(`[searchByRegion] Error: ${errorMessage}`);
      set({ isSearching: false, searchError: errorMessage });
    }
  },

  /**
   * Execute a country search using pre-fetched station data.
   * Requirements: 5.1, 5.2, 5.3, 5.5, 5.6, 9.1, 9.2, 9.3, 9.4
   */
  searchByCountry: (country: string, stations: WeatherStation[], state?: string, city?: string) => {
    log.debug(`🔍 [searchByCountry] Searching: country=${country}, state=${state || ''}, city=${city || ''}, stations=${stations.length}`);

    // Own the latest generation so any in-flight coordinate/station fetch that
    // resolves after this synchronous country search can't overwrite its results.
    bumpSearchGeneration();
    // Name the MOST SPECIFIC selection — an Area search that finds nothing should
    // say so about the area, not about the whole country.
    set({
      isSearching: true,
      searchError: null,
      searchYieldedNoResults: false,
      lastQueryLabel: `in ${city || state || country}`,
    });

    try {
      // Requirements: 5.1, 5.2 — filter by most specific selection
      let filtered = stations;

      if (state) {
        filtered = filtered.filter(s => s.state === state);
      }

      if (city) {
        filtered = filtered.filter(s => s.tags?.city === city);
      }

      log.info(`✅ [searchByCountry] Found ${filtered.length} stations`);

      // Cross-store: update station data
      useStationDataStore.getState().setSelectedStations(filtered);

      // Requirements: 5.3, 9.2 — store results and search state.
      //
      // A country/region/area that contains no stations is a ZERO-RESULT search,
      // not a fresh page: without this flag SearchPage fell through to the
      // pre-search prompt ("Select countries, regions, or areas above"), which
      // reads as if the selection never registered. Mirrors the coordinates and
      // station-tag paths so every method reports emptiness the same way.
      set({
        countrySearchState: {
          selectedCountry: country,
          selectedState: state || '',
          selectedCity: city || '',
        },
        isSearching: false,
        searchError: null,
        searchYieldedNoResults: filtered.length === 0,
      });

      // Dispatch fitStationBounds event if we have results
      if (filtered.length > 0) {
        window.dispatchEvent(new CustomEvent('fitStationBounds', {
          detail: { stations: filtered, padding: 50 },
        }));
      }

      // Clear map selection state
      useMapStore.getState().selectStation(null);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Country search failed. Please try again.';
      log.error(`❌ [searchByCountry] Error: ${errorMessage}`);

      set({ isSearching: false, searchError: errorMessage });
    }
  },

  /** Execute a unified station/tag search delegating to the appropriate API method.
   * Requirements: 3.1, 3.2, 3.3, 3.4, 3.5, 3.6, 3.7, 6.1, 6.2, 6.3, 6.4, 6.5 */
  searchByStationTag: async (searchType: StationSearchType, values: string[]) => {
    if (values.length === 0) return;

    const gen = bumpSearchGeneration();
    // Keep existing results visible under the spinner during a refine; replace only
    // on this generation's success. (Was clearing here → results flashed empty.)
    set({
      isSearching: true,
      searchError: null,
      searchYieldedNoResults: false,
      lastQueryLabel: `for ${describeSearchValues(searchType, values)}`,
    });

    const stationData = useStationDataStore.getState();

    try {
      const api = weatherStationAPI;
      const validStations: WeatherStation[] = [];

      if (searchType === 'stationCode') {
        // Requirements: 3.1, 3.4 — Station code search: batch in groups of 50
        const batchSize = 50;

        for (let i = 0; i < values.length; i += batchSize) {
          const batch = values.slice(i, i + batchSize);
          log.debug(`[searchByStationTag] Fetching station code batch ${Math.floor(i / batchSize) + 1}/${Math.ceil(values.length / batchSize)}: ${batch.length} codes`);

          try {
            const batchResults = await api.getStationsByCodes(batch);
            validStations.push(...batchResults);
          } catch {
            log.warn(`⚠️ [searchByStationTag] Batch fetch failed, skipping`);
          }

          if (i + batchSize < values.length) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }

        // Some values pasted into this box are a station's Davis ID or DTN Legacy
        // ID rather than its stationCode (both are shown as their own table columns,
        // so users copy-paste them here expecting it to just work). Those get no
        // match above (an exact stationCode lookup), so retry the leftovers as a
        // wildcard tag lookup — the same one "All Tags" mode uses — before giving up.
        const matchedCodes = new Set(validStations.map(s => s.stationCode.toLowerCase()));
        const unmatched = values.filter(v => !matchedCodes.has(v.toLowerCase()));
        if (unmatched.length > 0) {
          log.debug(`[searchByStationTag] ${unmatched.length} value(s) didn't match a stationCode — retrying as tag identifiers (Davis ID / DTN Legacy ID / etc.): ${unmatched.join(', ')}`);
          try {
            const tagMatches = await api.getStationsByTags('', unmatched);
            for (const station of tagMatches) {
              if (!matchedCodes.has(station.stationCode.toLowerCase())) {
                matchedCodes.add(station.stationCode.toLowerCase());
                validStations.push(station);
              }
            }
          } catch {
            log.warn(`⚠️ [searchByStationTag] Tag-identifier fallback failed, skipping`);
          }
        }
      } else if (searchType === 'deviceId') {
        // A device's Station ID (e.g. "3772", "UKI0000EGLL", "037720-99999") is not a
        // backend-searchable station tag — it lives inside each station's devices[].
        // Match it client-side against the already-loaded station list (allStations,
        // fetched at app init) which carries every station's devices[].
        //
        // The identifier the Devices panel shows as "Station ID" is `sourceStationId`
        // (the per-network id), while `stationId` mirrors the station code. Match on
        // BOTH so either value works. Case-insensitive exact match; a station matches
        // if ANY of its devices does.
        const wanted = new Set(values.map(v => v.trim().toLowerCase()).filter(Boolean));
        const allStations = useStationDataStore.getState().allStations;
        const seen = new Set<string>();
        for (const station of allStations) {
          const hit = station.devices?.some(d =>
            (d.sourceStationId && wanted.has(d.sourceStationId.toLowerCase())) ||
            (d.stationId && wanted.has(d.stationId.toLowerCase()))
          );
          if (hit && !seen.has(station.stationCode)) {
            seen.add(station.stationCode);
            validStations.push(station);
          }
        }
        log.debug(`[searchByStationTag] Device ID search matched ${validStations.length} stations across ${allStations.length} loaded`);
      } else {
        // Requirements: 3.2, 3.3 — Tag-based search: delegate to getStationsByTags
        const tagKey = mapSearchTypeToTagKey(searchType);
        log.debug(`[searchByStationTag] Searching by tag: key="${tagKey}", values=${values.join(',')}`);

        const stations = await api.getStationsByTags(tagKey, values);
        validStations.push(...stations);
      }

      if (gen !== searchGeneration) return; // superseded by a newer search / clear

      // Requirements: 6.1 — No results is an EMPTY STATE, not an error. Previously
      // this threw STATION_NOT_FOUND → amber error banner; the shared empty state
      // (searchYieldedNoResults) matches the coordinates method's zero-result UX.
      if (validStations.length === 0) {
        stationData.setSelectedStation(null);
        stationData.setSelectedStations([]);
        set({ isSearching: false, searchError: null, searchYieldedNoResults: true });
        return;
      }

      log.info(`✅ [searchByStationTag] Found ${validStations.length} stations`);

      // Cross-store: update station data
      stationData.setSelectedStation(validStations[0]);
      stationData.setSelectedStations(validStations);

      set({ isSearching: false, searchError: null, searchYieldedNoResults: false });

      // Requirements: 3.7 — Dispatch fitStationBounds event to fit map viewport
      window.dispatchEvent(new CustomEvent('fitStationBounds', {
        detail: { stations: validStations, padding: 50 },
      }));
    } catch (error) {
      if (gen !== searchGeneration) return; // superseded — don't surface a stale error
      let errorMessage = 'An unexpected error occurred';
      let shouldShowRetry = false;

      if (error instanceof APIError) {
        errorMessage = error.message;
        shouldShowRetry = error.type === ErrorType.NETWORK_ERROR ||
                         error.type === ErrorType.SERVICE_UNAVAILABLE ||
                         error.type === ErrorType.AUTHENTICATION_FAILED;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      set({ isSearching: false, searchError: errorMessage });

      if (onError && shouldShowRetry) {
        onError(errorMessage, 'Retry', () => { get().searchByStationTag(searchType, values); });
      }
    }
  },

  // Clear search results and reset search state
  // Task 3.1: Also clear countrySearchState (Requirements: 9.3)
  clearSearch: () => {
    // Invalidate any in-flight search so its late resolution can't repopulate
    // results or leave isSearching stuck true after the user cleared.
    bumpSearchGeneration();
    set({
      searchQuery: '',
      isSearching: false,
      searchError: null,
      searchCenter: null,
      searchRegion: null,
      searchYieldedNoResults: false,
      lastQueryLabel: null,
      countrySearchState: null,
      countryBoundaryLayers: null,
    });

    // Cross-store: clear station data and table highlight
    useStationDataStore.getState().setSelectedStation(null);
    useStationDataStore.getState().setSelectedStations([]);
    useTableStore.getState().highlightRow(null);
  },
}));
